import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

// Layout (direkte Imports - werden immer benötigt)
import { AppLayout, BuyerLayout } from '../components/layout';

// Common (direkte Imports - klein und kritisch)
import { Icon } from '../components/common';
import logo from '../assets/logo.svg';

// Auth Pages (lazy - selten nach erstem Besuch)
const Login = lazy(() => import('../pages/auth/Login'));
const Register = lazy(() => import('../pages/auth/Register'));
const Onboarding = lazy(() => import('../pages/auth/Onboarding'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const AuthorizeConsent = lazy(() => import('../pages/oauth/AuthorizeConsent'));

// Protected Pages (lazy - nur für eingeloggte Nutzer)
const MyStore = lazy(() => import('../pages/store/MyStore'));
const AddProduct = lazy(() => import('../pages/store/AddProduct'));
const EditProduct = lazy(() => import('../pages/store/EditProduct'));
const EarningsDashboard = lazy(() => import('../pages/earnings/EarningsDashboard'));
const PromotionHub = lazy(() => import('../pages/promotion/PromotionHub'));
const Messages = lazy(() => import('../pages/Messages'));
const Settings = lazy(() => import('../pages/settings/Settings'));
const InvoiceView = lazy(() => import('../pages/billing/InvoiceView'));

// Public Pages (lazy)
const PublicStore = lazy(() => import('../pages/public/PublicStore'));
const PublicProduct = lazy(() => import('../pages/public/PublicProduct'));
const LandingPage = lazy(() => import('../pages/public/LandingPage'));
const LandingEbook = lazy(() => import('../pages/public/LandingEbook'));
const LandingDigitaleProdukte = lazy(() => import('../pages/public/LandingDigitaleProdukte'));
const LandingKursplattform = lazy(() => import('../pages/public/LandingKursplattform'));
const LandingDigistore24Alternative = lazy(() => import('../pages/public/LandingDigistore24Alternative'));
const LandingElopageAlternative = lazy(() => import('../pages/public/LandingElopageAlternative'));
const LandingGumroadAlternative = lazy(() => import('../pages/public/LandingGumroadAlternative'));
const LandingCopecartAlternative = lazy(() => import('../pages/public/LandingCopecartAlternative'));
const LandingStanStoreAlternative = lazy(() => import('../pages/public/LandingStanStoreAlternative'));
const LandingPdfVerkaufen = lazy(() => import('../pages/public/LandingPdfVerkaufen'));
const LandingNotionTemplatesVerkaufen = lazy(() => import('../pages/public/LandingNotionTemplatesVerkaufen'));
const LandingAffiliatePlattform = lazy(() => import('../pages/public/LandingAffiliatePlattform'));
const LandingCreatorWerben = lazy(() => import('../pages/public/LandingCreatorWerben'));
const LandingEmpfehlungsprogramm = lazy(() => import('../pages/public/LandingEmpfehlungsprogramm'));
const LandingPassivesEinkommen = lazy(() => import('../pages/public/LandingPassivesEinkommen'));
const LandingAffiliateOhneProdukte = lazy(() => import('../pages/public/LandingAffiliateOhneProdukte'));
const LandingKiProdukteVerkaufen = lazy(() => import('../pages/public/LandingKiProdukteVerkaufen'));
const LandingPromptsVerkaufen = lazy(() => import('../pages/public/LandingPromptsVerkaufen'));
const LandingKiSoftwareVerkaufen = lazy(() => import('../pages/public/LandingKiSoftwareVerkaufen'));
const LandingMcpServerVerkaufen = lazy(() => import('../pages/public/LandingMcpServerVerkaufen'));
const LandingPromptbaseAlternative = lazy(() => import('../pages/public/LandingPromptbaseAlternative'));
const LandingMitKiGeldVerdienen = lazy(() => import('../pages/public/LandingMitKiGeldVerdienen'));
const MagazinIndex = lazy(() => import('../pages/public/MagazinIndex'));
const MagazinArticle = lazy(() => import('../pages/public/MagazinArticle'));
const LandingLongTail = lazy(() => import('../pages/public/LandingLongTail'));
const LandingMcpDeveloper = lazy(() => import('../pages/public/LandingMcpDeveloper'));
const LandingMcpEcommerce = lazy(() => import('../pages/public/LandingMcpEcommerce'));
const LandingMitKiProdukteErstellenUndVerkaufen = lazy(() => import('../pages/public/LandingMitKiProdukteErstellenUndVerkaufen'));
const ApiDocs = lazy(() => import('../pages/public/ApiDocs'));

// Englische SEO-Landingpages (lazy — nur im /en-Kontext gerendert)
const LandingSellDigitalProducts = lazy(() => import('../pages/public/en/LandingSellDigitalProducts'));
const LandingSellEbooks = lazy(() => import('../pages/public/en/LandingSellEbooks'));
const LandingGumroadAlternativeEn = lazy(() => import('../pages/public/en/LandingGumroadAlternativeEn'));
const LandingSellAiPrompts = lazy(() => import('../pages/public/en/LandingSellAiPrompts'));
const LandingSellNotionTemplates = lazy(() => import('../pages/public/en/LandingSellNotionTemplates'));
const LandingSellMcpServers = lazy(() => import('../pages/public/en/LandingSellMcpServers'));
const LandingReferCreators = lazy(() => import('../pages/public/en/LandingReferCreators'));
const LandingCreatorReferralProgram = lazy(() => import('../pages/public/en/LandingCreatorReferralProgram'));
const LandingPassiveIncome = lazy(() => import('../pages/public/en/LandingPassiveIncome'));
const LandingAffiliateWithoutProducts = lazy(() => import('../pages/public/en/LandingAffiliateWithoutProducts'));
const CheckoutSuccess = lazy(() => import('../pages/public/CheckoutSuccess'));
const DownloadVerify = lazy(() => import('../pages/public/DownloadVerify'));
const Hilfe = lazy(() => import('../pages/public/Hilfe'));

// Legal Pages (lazy)
const Impressum = lazy(() => import('../pages/legal/Impressum'));
const Datenschutz = lazy(() => import('../pages/legal/Datenschutz'));
const AGB = lazy(() => import('../pages/legal/AGB'));
const Widerruf = lazy(() => import('../pages/legal/Widerruf'));
const ContentPolicy = lazy(() => import('../pages/legal/ContentPolicy'));

// Report Pages (lazy)
const ReportContent = lazy(() => import('../pages/public/ReportContent'));

// Dashboard Pages (lazy)
const Purchases = lazy(() => import('../pages/dashboard/Purchases'));
const NewsletterDashboard = lazy(() => import('../pages/dashboard/NewsletterDashboard'));
const NewsletterCompose = lazy(() => import('../pages/dashboard/NewsletterCompose'));

/**
 * Protected Route Wrapper
 * Redirects to login if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * App Routes Configuration
 */
function AppRoutes() {
  const { user, loading, isAuthenticated } = useAuth();
  // Sprache = URL-Präfix (Router-basename). SEO-Landingpages haben pro
  // Sprache eigene Keyword-Slugs und werden nur im jeweiligen Kontext
  // gemountet — /en/pdf-verkaufen soll z.B. bewusst im 404 landen.
  const { i18n } = useTranslation();
  const lang = i18n.language || 'de';

  // Global Loading State
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* ================================
          LANDING PAGE (for non-authenticated users)
          ================================ */}
      
      <Route 
        path="/" 
        element={
          isAuthenticated 
            ? <Navigate to="/dashboard" replace /> 
            : <LandingPage />
        } 
      />

      {/* ================================
          PUBLIC ROUTES (no auth required)
          ================================ */}
      
      {/* Public Product Page */}
      <Route path="/p/:productId" element={<PublicProduct />} />
      
      {/* Public Store Page */}
      <Route path="/store/:username" element={<PublicStore />} />

      {/* ================================
          CHECKOUT ROUTES
          ================================ */}

      {/* Checkout Success - Nach erfolgreicher Stripe-Zahlung */}
      {/* Öffentlich für Gast-Checkout - Validierung über session_id */}
      <Route path="/checkout/success" element={<CheckoutSuccess />} />

      {/* ================================
          DOWNLOAD ROUTES
          ================================ */}

      {/* Download mit E-Mail-Verifikation (aus Kaufbestätigungs-E-Mails) */}
      <Route path="/download/:token" element={<DownloadVerify />} />

      {/* ================================
          LEGAL PAGES (§ 5 TMG, DSGVO)
          ================================ */}
      
      {/* Impressum - Pflicht nach § 5 TMG */}
      <Route path="/impressum" element={<Impressum />} />
      
      {/* Datenschutzerklärung - Pflicht nach DSGVO */}
      <Route path="/datenschutz" element={<Datenschutz />} />
      
      {/* AGB */}
      <Route path="/agb" element={<AGB />} />
      
      {/* Widerrufsbelehrung */}
      <Route path="/widerruf" element={<Widerruf />} />

      {/* Inhaltsrichtlinien (DSA-konform) */}
      <Route path="/inhaltsrichtlinien" element={<ContentPolicy />} />
      <Route path="/content-policy" element={<Navigate to="/inhaltsrichtlinien" replace />} />

      {/* Meldeformular (DSA Art. 16) */}
      <Route path="/melden" element={<ReportContent />} />
      <Route path="/report" element={<Navigate to="/melden" replace />} />

      {/* SEO-Nischen-Landingpages (nur im deutschen Kontext —
          englische Pendants haben eigene Keyword-Slugs, s.u.) */}
      {lang === 'de' && (
        <>
      <Route path="/ebook-verkaufen" element={<LandingEbook />} />
      <Route path="/digitale-produkte-verkaufen" element={<LandingDigitaleProdukte />} />
      <Route path="/kursplattform" element={<LandingKursplattform />} />
      <Route path="/digistore24-alternative" element={<LandingDigistore24Alternative />} />
      <Route path="/elopage-alternative" element={<LandingElopageAlternative />} />
      <Route path="/gumroad-alternative" element={<LandingGumroadAlternative />} />
      <Route path="/copecart-alternative" element={<LandingCopecartAlternative />} />
      <Route path="/stan-store-alternative-deutsch" element={<LandingStanStoreAlternative />} />
      <Route path="/pdf-verkaufen" element={<LandingPdfVerkaufen />} />
      <Route path="/notion-templates-verkaufen" element={<LandingNotionTemplatesVerkaufen />} />
      <Route path="/affiliate-plattform" element={<LandingAffiliatePlattform />} />
      <Route path="/creator-werben" element={<LandingCreatorWerben />} />
      {/* Referral-Cluster — Empfehlungsprogramm & passives Einkommen */}
      <Route path="/empfehlungsprogramm" element={<LandingEmpfehlungsprogramm />} />
      <Route path="/passives-einkommen-online" element={<LandingPassivesEinkommen />} />
      <Route path="/affiliate-ohne-eigene-produkte" element={<LandingAffiliateOhneProdukte />} />
      {/* KI-Nische — Hub + Anker + Alternative */}
      <Route path="/ki-produkte-verkaufen" element={<LandingKiProdukteVerkaufen />} />
      <Route path="/prompts-verkaufen" element={<LandingPromptsVerkaufen />} />
      <Route path="/ki-software-verkaufen" element={<LandingKiSoftwareVerkaufen />} />
      <Route path="/mcp-server-verkaufen" element={<LandingMcpServerVerkaufen />} />
      <Route path="/promptbase-alternative-deutsch" element={<LandingPromptbaseAlternative />} />
      {/* KI-Nische — Pillar-Content (Cluster B) */}
      <Route path="/mit-ki-geld-verdienen" element={<LandingMitKiGeldVerdienen />} />
      {/* Magazin — Pillar/Cluster-Content, DE-only */}
      <Route path="/magazin" element={<MagazinIndex />} />
      <Route path="/magazin/:slug" element={<MagazinArticle />} />
      {/* Long-Tail-Pattern (config-driven) */}
      <Route path="/ebook-verkaufen-ohne-webseite" element={<LandingLongTail slug="ebook-verkaufen-ohne-webseite" />} />
      <Route path="/kurs-verkaufen-ohne-webseite" element={<LandingLongTail slug="kurs-verkaufen-ohne-webseite" />} />
      <Route path="/pdf-verkaufen-ohne-webseite" element={<LandingLongTail slug="pdf-verkaufen-ohne-webseite" />} />
      <Route path="/canva-templates-verkaufen" element={<LandingLongTail slug="canva-templates-verkaufen" />} />
      <Route path="/excel-templates-verkaufen" element={<LandingLongTail slug="excel-templates-verkaufen" />} />
      <Route path="/chatgpt-prompts-verkaufen" element={<LandingLongTail slug="chatgpt-prompts-verkaufen" />} />
      <Route path="/midjourney-prompts-verkaufen" element={<LandingLongTail slug="midjourney-prompts-verkaufen" />} />
      <Route path="/custom-gpt-verkaufen" element={<LandingLongTail slug="custom-gpt-verkaufen" />} />
      <Route path="/n8n-workflows-verkaufen" element={<LandingLongTail slug="n8n-workflows-verkaufen" />} />
      {/* Developer-Landing mit deutschem Keyword-Slug */}
      <Route path="/mcp-fuer-ecommerce" element={<LandingMcpEcommerce />} />
      <Route path="/mit-ki-produkte-erstellen-und-verkaufen" element={<LandingMitKiProdukteErstellenUndVerkaufen />} />
        </>
      )}

      {/* Englische SEO-Landingpages (nur im /en-Kontext — Router läuft mit
          basename=/en, daher matchen die Slugs hier präfixfrei) */}
      {lang === 'en' && (
        <>
      <Route path="/sell-digital-products" element={<LandingSellDigitalProducts />} />
      <Route path="/sell-ebooks-online" element={<LandingSellEbooks />} />
      <Route path="/gumroad-alternative" element={<LandingGumroadAlternativeEn />} />
      <Route path="/sell-ai-prompts" element={<LandingSellAiPrompts />} />
      <Route path="/sell-notion-templates" element={<LandingSellNotionTemplates />} />
      <Route path="/sell-mcp-servers" element={<LandingSellMcpServers />} />
      <Route path="/refer-creators" element={<LandingReferCreators />} />
      <Route path="/creator-referral-program" element={<LandingCreatorReferralProgram />} />
      <Route path="/passive-income-for-creators" element={<LandingPassiveIncome />} />
      <Route path="/affiliate-without-own-products" element={<LandingAffiliateWithoutProducts />} />
        </>
      )}

      {/* Developer-Landing — MCP-Server & Public Creator API (sprachneutrale Slugs) */}
      <Route path="/mcp" element={<LandingMcpDeveloper />} />
      <Route path="/api-docs" element={<ApiDocs />} />

      {/* Hilfe / FAQ */}
      <Route path="/hilfe" element={<Hilfe />} />
      <Route path="/faq" element={<Navigate to="/hilfe" replace />} />
      <Route path="/help" element={<Navigate to="/hilfe" replace />} />

      {/* Rechnungen - öffentlich via Access-Token, kein Login nötig */}
      <Route path="/invoice/:token" element={<InvoiceView />} />

      {/* Legacy routes for consistent linking */}
      <Route path="/privacy" element={<Navigate to="/datenschutz" replace />} />
      <Route path="/terms" element={<Navigate to="/agb" replace />} />

      {/* ================================
          AUTH ROUTES
          ================================ */}
      
      {/* Login */}
      <Route 
        path="/login" 
        element={
          isAuthenticated 
            ? <Navigate to="/dashboard" replace /> 
            : <Login />
        } 
      />
      
      {/* Register */}
      <Route 
        path="/register" 
        element={
          isAuthenticated 
            ? <Navigate to="/dashboard" replace /> 
            : <Register />
        } 
      />
      
      {/* Onboarding (optional) */}
      <Route
        path="/onboarding"
        element={
          isAuthenticated
            ? <Onboarding />
            : <Navigate to="/login" replace />
        }
      />

      {/* Passwort vergessen */}
      <Route
        path="/forgot-password"
        element={
          isAuthenticated
            ? <Navigate to="/dashboard" replace />
            : <ForgotPassword />
        }
      />

      {/* Passwort zurücksetzen (Link aus E-Mail) */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* OAuth Consent — claude.ai Custom Connector flow */}
      <Route path="/oauth/authorize" element={<AuthorizeConsent />} />

      {/* ================================
          PROTECTED ROUTES - BUYER ONLY
          Minimales Layout für User ohne eigene Produkte
          ================================ */}

      {user?.isBuyerOnly && (
        <Route
          element={
            <ProtectedRoute>
              <BuyerLayout />
            </ProtectedRoute>
          }
        >
          {/* Käufer sehen nur ihre Käufe */}
          <Route path="/dashboard" element={<Navigate to="/dashboard/purchases" replace />} />
          <Route path="/dashboard/purchases" element={<Purchases />} />

          {/* Produkt erstellen (Upgrade zu Creator) */}
          <Route path="/products/new" element={<AddProduct />} />
        </Route>
      )}

      {/* ================================
          PROTECTED ROUTES - FULL APP
          Volles Layout für Creator/Seller
          ================================ */}

      {!user?.isBuyerOnly && (
        <Route
          element={
            <ProtectedRoute>
              <AppLayout user={user} />
            </ProtectedRoute>
          }
        >
          {/* Dashboard / Home (authenticated) */}
          <Route path="/dashboard" element={<MyStore />} />

          {/* Product Management */}
          <Route path="/products/new" element={<AddProduct />} />
          <Route path="/products/:id/edit" element={<EditProduct />} />

          {/* Newsletter Management */}
          <Route path="/products/:productId/newsletter" element={<NewsletterDashboard />} />
          <Route path="/products/:productId/newsletter/compose" element={<NewsletterCompose />} />
          <Route path="/products/:productId/newsletter/compose/:issueId" element={<NewsletterCompose />} />

          {/* Earnings / Statistics */}
          <Route path="/earnings" element={<EarningsDashboard />} />

          {/* Promotion */}
          <Route path="/promotion" element={<PromotionHub />} />

          {/* Messages / Notifications */}
          <Route path="/messages" element={<Messages />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />

          {/* Purchases / My bought products */}
          <Route path="/dashboard/purchases" element={<Purchases />} />
        </Route>
      )}

      {/* ================================
          FALLBACK ROUTES
          ================================ */}
      
      {/* Legacy/Share-Format: /@username → /store/username
          Einzelnes dynamisches Segment, da React Router v6 ein partielles
          Segment wie "/@:username" NICHT matcht. Statische Routen behalten
          dank Specificity-Ranking Vorrang; Nicht-@-Pfade fallen auf 404. */}
      <Route path="/:handle" element={<AtHandleRoute />} />

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

/**
 * Loading Screen Component
 */
function LoadingScreen() {
  return (
    <div 
      style={{ 
        minHeight: '100dvh',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '20px',
        background: 'var(--color-bg-primary)'
      }}
    >
      <img src={logo} alt="MoneMee" width="56" height="56" style={{ borderRadius: '14px' }} />
      <div 
        style={{ 
          width: '32px', 
          height: '32px', 
          border: '3px solid var(--color-border)',
          borderTopColor: 'var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
      />
    </div>
  );
}

/**
 * Handler für einzelnes Top-Level-Segment.
 * Leitet das Share-Format /@username auf die kanonische Store-Route um;
 * alle anderen unbekannten Einzelsegmente landen regulär im 404.
 */
function AtHandleRoute() {
  const { handle } = useParams();
  if (handle && handle.startsWith('@') && handle.length > 1) {
    return <Navigate to={`/store/${handle.slice(1)}`} replace />;
  }
  return <NotFound />;
}

/**
 * 404 Not Found Component
 */
function NotFound() {
  const { t } = useTranslation();
  return (
    <div 
      style={{
        minHeight: '100dvh',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '24px',
        padding: '24px',
        textAlign: 'center',
        background: 'var(--color-bg-primary)'
      }}
    >
      <div 
        style={{ 
          width: '80px',
          height: '80px',
          background: 'var(--color-bg-secondary)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-tertiary)'
        }}
      >
        <Icon name="searchX" size={40} />
      </div>
      <div>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          color: 'var(--color-text-primary)',
          marginBottom: '8px'
        }}>
          {t('notFound.title')}
        </h1>
        <p style={{
          color: 'var(--color-text-secondary)',
          marginBottom: '24px'
        }}>
          {t('notFound.description')}
        </p>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: '12px',
            textDecoration: 'none',
            fontWeight: '500'
          }}
        >
          <Icon name="home" size={18} />
          {t('notFound.backHome')}
        </Link>
      </div>
    </div>
  );
}

export default AppRoutes;
